import React, { useState, useEffect } from "react";
import styled from "@emotion/styled";
import { UIBlockData } from "./UIBlockRenderer";
import { BlockPicker } from "./BlockPicker";
import { UIBlock } from "../../api/uiBlocksApi";

const EditorContainer = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  width: 450px;
  height: 100vh;
  background: white;
  border-left: 1px solid #e1e5e9;
  display: flex;
  flex-direction: column;
  z-index: 1000;
  transform: translateX(${(props) => (props.isOpen ? "0" : "100%")});
  transition: transform 0.3s ease-in-out;
  box-shadow: -2px 0 10px rgba(0, 0, 0, 0.1);
`;

const EditorHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid #e1e5e9;
  background: #f8f9fa;
`;

const EditorTitle = styled.h3`
  margin: 0 0 8px 0;
  color: #1f2937;
  font-size: 18px;
  font-weight: 600;
`;

const BlockInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const BlockName = styled.span`
  color: #6b7280;
  font-size: 14px;
`;

const ContainerBadge = styled.span<{ isContainer: boolean }>`
  background: ${(props) => (props.isContainer ? "#10b981" : "#f3f4f6")};
  color: ${(props) => (props.isContainer ? "white" : "#374151")};
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: none;
  font-size: 20px;
  color: #6b7280;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s;

  &:hover {
    background: #f3f4f6;
    color: #374151;
  }
`;

const EditorContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px;
`;

const EditorActions = styled.div`
  padding: 16px 20px;
  border-top: 1px solid #e1e5e9;
  background: #f8f9fa;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const ActionButton = styled.button<{
  variant?: "primary" | "secondary" | "success";
}>`
  padding: 8px 16px;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  font-size: 14px;

  ${(props) => {
    switch (props.variant) {
      case "primary":
        return `
          background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
          color: white;
          &:hover {
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
          }
        `;
      case "success":
        return `
          background: linear-gradient(135deg, #10b981 0%, #047857 100%);
          color: white;
          &:hover {
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4);
          }
        `;
      default:
        return `
          background: white;
          color: #374151;
          border: 1px solid #d1d5db;
          &:hover {
            background: #f9fafb;
          }
        `;
    }
  }}
`;

const FormSection = styled.div`
  margin-bottom: 24px;
`;

const SectionTitle = styled.h4`
  margin: 0 0 12px 0;
  color: #1f2937;
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
`;

const Label = styled.label`
  font-weight: 500;
  color: #374151;
  font-size: 14px;
`;

const Input = styled.input`
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const Textarea = styled.textarea`
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  resize: vertical;
  min-height: 80px;
  font-family: inherit;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const ColorInput = styled.input`
  padding: 4px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  width: 80px;
  height: 40px;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const HelpText = styled.p`
  margin: 0;
  font-size: 12px;
  color: #6b7280;
  line-height: 1.4;
`;

const SlotContainer = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
  background: #f9fafb;
`;

const SlotHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const SlotTitle = styled.h5`
  margin: 0;
  color: #1f2937;
  font-size: 14px;
  font-weight: 600;
`;

const SlotBadge = styled.span<{ isRepeatable: boolean }>`
  background: ${(props) => (props.isRepeatable ? "#3b82f6" : "#6b7280")};
  color: white;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
`;

const SlotDescription = styled.p`
  margin: 0 0 12px 0;
  font-size: 12px;
  color: #6b7280;
`;

const SlotActions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const SmallButton = styled.button<{
  variant?: "primary" | "success" | "secondary";
}>`
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  border: none;
  transition: all 0.2s;

  ${(props) => {
    switch (props.variant) {
      case "primary":
        return `
          background: #3b82f6;
          color: white;
          &:hover { background: #2563eb; }
        `;
      case "success":
        return `
          background: #10b981;
          color: white;
          &:hover { background: #059669; }
        `;
      default:
        return `
          background: #f3f4f6;
          color: #374151;
          &:hover { background: #e5e7eb; }
        `;
    }
  }}
`;

const SlotItemsList = styled.div`
  margin-top: 12px;
`;

const SlotItem = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  padding: 8px 12px;
  margin-bottom: 8px;
  display: flex;
  justify-content: between;
  align-items: center;
  font-size: 12px;
`;

const SlotItemContent = styled.span`
  flex: 1;
  color: #374151;
`;

const SlotItemActions = styled.div`
  display: flex;
  gap: 4px;
`;

interface SlotItemData {
  id: string;
  content: string;
  type: "text" | "block";
  blockId?: string;
  blockData?: UIBlock;
  overrides?: Record<string, any>;
}

interface SlotData {
  id: string;
  name: string;
  displayName: string;
  items: SlotItemData[];
}

interface EnhancedBlockEditorProps {
  block: UIBlockData | null;
  overrides: Record<string, any>;
  slots: Record<string, SlotData>;
  isOpen: boolean;
  onClose: () => void;
  onSave: (
    overrides: Record<string, any>,
    slots: Record<string, SlotData>
  ) => void;
  onAddSlotItem: (slotName: string, type: "text" | "block") => void;
}

export const EnhancedBlockEditor: React.FC<EnhancedBlockEditorProps> = ({
  block,
  overrides,
  slots,
  isOpen,
  onClose,
  onSave,
  onAddSlotItem,
}) => {
  const [localOverrides, setLocalOverrides] = useState<Record<string, any>>({});
  const [localSlots, setLocalSlots] = useState<Record<string, SlotData>>({});
  const [blockPickerOpen, setBlockPickerOpen] = useState(false);
  const [selectedSlotForBlock, setSelectedSlotForBlock] = useState<string | null>(null);

  useEffect(() => {
    setLocalOverrides(overrides || {});
    setLocalSlots(slots || {});
  }, [overrides, slots, block]);

  if (!block) return null;

  const handleInputChange = (field: string, value: any) => {
    setLocalOverrides((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = () => {
    onSave(localOverrides, localSlots);
    onClose();
  };

  const handleCancel = () => {
    setLocalOverrides(overrides || {});
    setLocalSlots(slots || {});
    onClose();
  };

  const handleAddSlotItem = (slotName: string, type: "text" | "block") => {
    if (type === "block") {
      setSelectedSlotForBlock(slotName);
      setBlockPickerOpen(true);
      return;
    }

    const newItem: SlotItemData = {
      id: `item-${Date.now()}`,
      content: "New content",
      type: "text",
    };

    setLocalSlots((prev) => ({
      ...prev,
      [slotName]: {
        ...prev[slotName],
        items: [...(prev[slotName]?.items || []), newItem],
      },
    }));
  };

  const handleBlockSelect = (selectedBlock: UIBlock) => {
    if (!selectedSlotForBlock) return;

    // Initialize block with schema defaults
    const defaultOverrides: Record<string, any> = {};
    Object.entries(selectedBlock.schema.editableFields || {}).forEach(
      ([key, defaultValue]) => {
        defaultOverrides[key] = defaultValue;
      }
    );

    const newItem: SlotItemData = {
      id: `item-${Date.now()}`,
      content: selectedBlock.name,
      type: "block",
      blockId: selectedBlock.id,
      blockData: selectedBlock,
      overrides: defaultOverrides,
    };

    setLocalSlots((prev) => ({
      ...prev,
      [selectedSlotForBlock]: {
        ...prev[selectedSlotForBlock],
        items: [...(prev[selectedSlotForBlock]?.items || []), newItem],
      },
    }));

    setBlockPickerOpen(false);
    setSelectedSlotForBlock(null);
  };

  const handleRemoveSlotItem = (slotName: string, itemId: string) => {
    setLocalSlots((prev) => ({
      ...prev,
      [slotName]: {
        ...prev[slotName],
        items: prev[slotName]?.items.filter((item) => item.id !== itemId) || [],
      },
    }));
  };

  const handleEditSlotItem = (
    slotName: string,
    itemId: string,
    content: string
  ) => {
    setLocalSlots((prev) => ({
      ...prev,
      [slotName]: {
        ...prev[slotName],
        items:
          prev[slotName]?.items.map((item) =>
            item.id === itemId ? { ...item, content } : item
          ) || [],
      },
    }));
  };

  // Get field type based on field name
  const getFieldType = (fieldName: string) => {
    const name = fieldName.toLowerCase();
    if (name.includes("color")) return "color";
    if (
      name.includes("text") ||
      name.includes("content") ||
      name.includes("description") ||
      name.includes("subtitle")
    )
      return "textarea";
    if (name.includes("url") || name.includes("link")) return "url";
    if (name.includes("email")) return "email";
    if (name.includes("phone")) return "tel";
    return "text";
  };

  const renderFieldInput = (fieldName: string, value: any) => {
    const fieldType = getFieldType(fieldName);

    switch (fieldType) {
      case "color":
        return (
          <ColorInput
            type="color"
            value={value || "#000000"}
            onChange={(e) => handleInputChange(fieldName, e.target.value)}
          />
        );
      case "textarea":
        return (
          <Textarea
            value={value || ""}
            onChange={(e) => handleInputChange(fieldName, e.target.value)}
            placeholder={`Enter ${fieldName}...`}
          />
        );
      default:
        return (
          <Input
            type={fieldType}
            value={value || ""}
            onChange={(e) => handleInputChange(fieldName, e.target.value)}
            placeholder={`Enter ${fieldName}...`}
          />
        );
    }
  };

  return (
    <EditorContainer isOpen={isOpen}>
      <EditorHeader>
        <CloseButton onClick={onClose}>×</CloseButton>
        <EditorTitle>Edit Block</EditorTitle>
        <BlockInfo>
          <BlockName>{block.name}</BlockName>
          <ContainerBadge isContainer={block.isContainer}>
            {block.isContainer ? "Container" : "Content"}
          </ContainerBadge>
        </BlockInfo>
      </EditorHeader>

      <EditorContent>
        {/* Editable Fields Section */}
        <FormSection>
          <SectionTitle>📝 Content Settings</SectionTitle>

          {Object.keys(block.schema.editableFields || {}).length === 0 ? (
            <HelpText>
              This block doesn't have any customizable content fields.
            </HelpText>
          ) : (
            Object.entries(block.schema.editableFields || {}).map(
              ([fieldName, defaultValue]) => (
                <FormGroup key={fieldName}>
                  <Label>
                    {fieldName.charAt(0).toUpperCase() +
                      fieldName.slice(1).replace(/([A-Z])/g, " $1")}
                  </Label>
                  {renderFieldInput(
                    fieldName,
                    localOverrides[fieldName] || defaultValue
                  )}
                </FormGroup>
              )
            )
          )}
        </FormSection>

        {/* Slots Section for Container Blocks */}
        {block.isContainer && block.slots && block.slots.length > 0 && (
          <FormSection>
            <SectionTitle>🧩 Container Slots</SectionTitle>
            <HelpText style={{ marginBottom: "16px" }}>
              Manage the content slots in this container block. Repeatable slots
              can have multiple items.
            </HelpText>

            {block.slots.map((slot) => {
              const slotData = localSlots[slot.name] || {
                id: slot.id,
                name: slot.name,
                displayName: slot.displayName,
                items: [],
              };

              return (
                <SlotContainer key={slot.id}>
                  <SlotHeader>
                    <SlotTitle>{slot.displayName}</SlotTitle>
                    <SlotBadge isRepeatable={slot.isRepeatable}>
                      {slot.isRepeatable ? "Repeatable" : "Single"}
                    </SlotBadge>
                  </SlotHeader>

                  <SlotDescription>
                    {/* {slot.description || "Descriptione"} */}
                    {slot.minItems && slot.minItems > 0 && (
                      <span> • Min items: {slot.minItems}</span>
                    )}
                    {slot.maxItems && (
                      <span> • Max items: {slot.maxItems}</span>
                    )}
                  </SlotDescription>

                  <SlotActions>
                    {slot.isRepeatable && (
                      <>
                        <SmallButton
                          variant="primary"
                          onClick={() => handleAddSlotItem(slot.name, "text")}
                        >
                          + Add Text
                        </SmallButton>
                        <SmallButton
                          variant="success"
                          onClick={() => handleAddSlotItem(slot.name, "block")}
                        >
                          + Add Block
                        </SmallButton>
                      </>
                    )}
                    {!slot.isRepeatable && slotData.items.length === 0 && (
                      <SmallButton
                        variant="primary"
                        onClick={() => handleAddSlotItem(slot.name, "block")}
                      >
                        Add Content
                      </SmallButton>
                    )}
                  </SlotActions>

                  {slotData.items.length > 0 && (
                    <SlotItemsList>
                      {slotData.items.map((item, index) => (
                        <SlotItem key={item.id}>
                          <SlotItemContent>
                            {index + 1}. {item.content} ({item.type})
                          </SlotItemContent>
                          <SlotItemActions>
                            <SmallButton
                              onClick={() => {
                                const newContent = prompt(
                                  "Edit content:",
                                  item.content
                                );
                                if (newContent !== null) {
                                  handleEditSlotItem(
                                    slot.name,
                                    item.id,
                                    newContent
                                  );
                                }
                              }}
                            >
                              Edit
                            </SmallButton>
                            <SmallButton
                              onClick={() =>
                                handleRemoveSlotItem(slot.name, item.id)
                              }
                            >
                              Remove
                            </SmallButton>
                          </SlotItemActions>
                        </SlotItem>
                      ))}
                    </SlotItemsList>
                  )}
                </SlotContainer>
              );
            })}
          </FormSection>
        )}
      </EditorContent>

      <EditorActions>
        <ActionButton onClick={handleCancel}>Cancel</ActionButton>
        <ActionButton variant="primary" onClick={handleSave}>
          Save Changes
        </ActionButton>
      </EditorActions>

      <BlockPicker
        isOpen={blockPickerOpen}
        onClose={() => {
          setBlockPickerOpen(false);
          setSelectedSlotForBlock(null);
        }}
        onSelectBlock={handleBlockSelect}
        allowedCategories={selectedSlotForBlock ? 
          block.slots?.find(slot => slot.name === selectedSlotForBlock)?.allowedChildren || [] 
          : undefined
        }
      />
    </EditorContainer>
  );
};
